import { Injectable, computed, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OnboardingApiService } from '../services/onboarding-api.service';
import type { OnboardingStatus } from '../models/onboarding-status.model';

const COMPLETED_STEP = 4;

@Injectable({ providedIn: 'root' })
export class OnboardingStore {
  private readonly statusSignal = signal<OnboardingStatus | null>(null);
  private readonly loadingSignal = signal(false);

  readonly status = this.statusSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly visible = computed(() => this.statusSignal()?.shouldShowTour === true);
  readonly currentStep = computed(() => (this.statusSignal()?.step ?? 0) + 1);

  constructor(private readonly api: OnboardingApiService) {}

  async loadStatus(): Promise<void> {
    this.loadingSignal.set(true);
    try {
      const r = await firstValueFrom(this.api.getStatus());
      this.statusSignal.set(r);
    } catch {
      // Silencioso: backend indisponivel / 404 / 500 nao pode quebrar a UI.
      // Tour fica invisivel ate proxima tentativa.
      this.statusSignal.set(null);
    } finally {
      this.loadingSignal.set(false);
    }
  }

  async advance(targetStep?: number): Promise<void> {
    const r = await firstValueFrom(this.api.advance(targetStep));
    this.statusSignal.update((s) =>
      s ? { ...s, step: r.step, shouldShowTour: r.step < COMPLETED_STEP } : s
    );
  }

  async snooze(): Promise<void> {
    await firstValueFrom(this.api.snooze());
    this.statusSignal.update((s) => (s ? { ...s, shouldShowTour: false } : s));
  }

  async skip(): Promise<void> {
    await firstValueFrom(this.api.skip());
    this.statusSignal.update((s) =>
      s ? { ...s, step: COMPLETED_STEP, shouldShowTour: false } : s
    );
  }
}
